import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { BusinessCard } from "@/components/BusinessCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Search, MapPin, Filter } from "lucide-react";

export default function DirectoryPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  const [q, setQ] = useState(params.get("q") || "");
  const [city, setCity] = useState(params.get("city") || "");
  const [category, setCategory] = useState(params.get("category") || "all");
  const [minRating, setMinRating] = useState([Number(params.get("min_rating") || 0)]);
  const [verifiedOnly, setVerifiedOnly] = useState(params.get("verified_only") === "1");
  const [featuredOnly, setFeaturedOnly] = useState(params.get("featured") === "1");
  const [professionalsOnly, setProfessionalsOnly] = useState(params.get("type") === "professional");
  const [sort, setSort] = useState(params.get("sort") || "featured");

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const query = {
      limit: 24,
      sort,
      ...(q ? { q } : {}),
      ...(city ? { city } : {}),
      ...(category && category !== "all" ? { category } : {}),
      ...(minRating[0] > 0 ? { min_rating: minRating[0] } : {}),
      ...(verifiedOnly ? { verified_only: true } : {}),
      ...(featuredOnly ? { featured_only: true } : {}),
      ...(professionalsOnly ? { is_professional: true } : {}),
    };
    api
      .get("/businesses", { params: query })
      .then((r) => {
        setItems(r.data.items);
        setTotal(r.data.total);
      })
      .finally(() => setLoading(false));
  }, [q, city, category, minRating, verifiedOnly, featuredOnly, professionalsOnly, sort]);

  const applyToUrl = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (city) p.set("city", city);
    if (category !== "all") p.set("category", category);
    if (minRating[0] > 0) p.set("min_rating", String(minRating[0]));
    if (verifiedOnly) p.set("verified_only", "1");
    if (featuredOnly) p.set("featured", "1");
    if (professionalsOnly) p.set("type", "professional");
    if (sort !== "featured") p.set("sort", sort);
    navigate(`/directory?${p.toString()}`, { replace: true });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="directory-page">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">Directory</h1>
        <p className="text-slate-500 mt-2">
          {loading ? "Loading listings…" : <span data-testid="directory-total"><span className="font-semibold text-slate-800">{total}</span> businesses & professionals matched</span>}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[280px,1fr] gap-8">
        {/* Filters */}
        <aside className="bg-white rounded-xl border border-slate-200 p-6 h-fit sticky top-24" data-testid="directory-filters">
          <div className="flex items-center gap-2 mb-6">
            <Filter className="h-4 w-4 text-blue-700" />
            <h2 className="text-base font-semibold text-slate-900">Filters</h2>
          </div>

          <label className="text-xs font-medium uppercase tracking-widest text-slate-500 mb-2 block">Search</label>
          <div className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 mb-5">
            <Search className="h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onBlur={applyToUrl} placeholder="Name, description…" className="border-0 focus-visible:ring-0 shadow-none px-1" data-testid="filter-query" />
          </div>

          <label className="text-xs font-medium uppercase tracking-widest text-slate-500 mb-2 block">City</label>
          <div className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 mb-5">
            <MapPin className="h-4 w-4 text-slate-400" />
            <Input value={city} onChange={(e) => setCity(e.target.value)} onBlur={applyToUrl} placeholder="e.g. Austin" className="border-0 focus-visible:ring-0 shadow-none px-1" data-testid="filter-city" />
          </div>

          <label className="text-xs font-medium uppercase tracking-widest text-slate-500 mb-2 block">Category</label>
          <Select value={category} onValueChange={(v) => { setCategory(v); }}>
            <SelectTrigger data-testid="filter-category-trigger" className="mb-5"><SelectValue placeholder="All categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="filter-category-all">All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.slug} value={c.slug} data-testid={`filter-category-${c.slug}`}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <label className="text-xs font-medium uppercase tracking-widest text-slate-500 mb-2 block">
            Minimum rating: <span className="text-slate-800 font-semibold">{minRating[0].toFixed(1)}</span>
          </label>
          <Slider value={minRating} onValueChange={setMinRating} min={0} max={5} step={0.5} className="mb-6" data-testid="filter-min-rating" />

          <label className="text-xs font-medium uppercase tracking-widest text-slate-500 mb-2 block">Sort by</label>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="filter-sort-trigger" className="mb-5"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="featured">Featured first</SelectItem>
              <SelectItem value="rating">Top rated</SelectItem>
              <SelectItem value="newest">Newest</SelectItem>
            </SelectContent>
          </Select>

          <div className="space-y-3 pt-2 border-t border-slate-200">
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <Checkbox checked={verifiedOnly} onCheckedChange={(v) => setVerifiedOnly(!!v)} data-testid="filter-verified" />
              Verified only
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <Checkbox checked={featuredOnly} onCheckedChange={(v) => setFeaturedOnly(!!v)} data-testid="filter-featured" />
              Featured / Premium only
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <Checkbox checked={professionalsOnly} onCheckedChange={(v) => setProfessionalsOnly(!!v)} data-testid="filter-professionals" />
              Individual professionals
            </label>
          </div>
        </aside>

        {/* Results */}
        <div>
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-72 rounded-xl bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center" data-testid="directory-empty">
              <div className="text-slate-500">No businesses match your filters.</div>
              <Button className="mt-4" variant="outline" onClick={() => { setQ(""); setCity(""); setCategory("all"); setMinRating([0]); setVerifiedOnly(false); setFeaturedOnly(false); setProfessionalsOnly(false); navigate("/directory"); }}>
                Reset filters
              </Button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="directory-results">
              {items.map((b) => <BusinessCard key={b.id} business={b} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
